// StudentAttendance.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { attendanceAPI } from '../services/api';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { 
  QrCodeScan, 
  CheckCircleFill, 
  ExclamationTriangleFill,
  Camera,
  Lightbulb,
  Phone, 
  CameraVideo,
  CheckCircle 
} from 'react-bootstrap-icons';
import '../CSS/StudentAttendance.css';

function StudentAttendance() {
  const [scanner, setScanner] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cameraPermission, setCameraPermission] = useState(null);
  const [showGeneralError, setShowGeneralError] = useState(false);
  const [generalErrorMessage, setGeneralErrorMessage] = useState('');
  
  const user = useSelector(state => state.auth.user);
  const scannerRef = useRef(null);

  // Check today's attendance on component mount
  useEffect(() => {
    checkTodayAttendance();
    return () => {
      // Cleanup scanner on unmount
      if (scannerRef.current) {
        scannerRef.current.clear();
      }
    };
  }, []);

    // Check for previously granted permission
useEffect(() => {
  const hasStoredPermission = localStorage.getItem('cameraPermissionGranted') === 'true';
  if (hasStoredPermission) {
    checkCameraPermissionSilently(); // ← Uses the silent version (no modal on failure)
  }
}, []);

  useEffect(() => {
  if (!scanning) return;

  // Wait for the #sa-qr-reader div to be in the DOM
  const timer = setTimeout(() => {
    const html5QrcodeScanner = new Html5QrcodeScanner(
      'sa-qr-reader',
      {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
        showTorchButtonIfSupported: true,
        rememberLastUsedCamera: true,
        supportedScanTypes: []
      },
      false
    );

    html5QrcodeScanner.render(
      (decodedText) => handleScan(decodedText),
      (error) => {
        if (!error.includes('NotFound')) {
          console.debug('QR Scanner:', error);
        }
      }
    );

    scannerRef.current = html5QrcodeScanner;
  }, 100); // small delay to ensure DOM has updated

  return () => clearTimeout(timer);
}, [scanning]);

  const checkTodayAttendance = async () => {
    try {
      const response = await attendanceAPI.getStudentTodayAttendance(user.id);
      setTodayAttendance(response.data.data);
    } catch (error) {
      console.error('Error checking attendance:', error);
    } finally {
      setLoading(false);
    }
  };

   const requestCameraPermission = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    stream.getTracks().forEach(track => track.stop());
    setCameraPermission(true);
    setShowError(false);
    localStorage.setItem('cameraPermissionGranted', 'true');
    setScanning(true); // ← just set state, don't call startScanner()
  } catch (error) {
    setCameraPermission(false);
    setErrorMessage('Camera access is required to scan QR codes. Please click the button below to grant permission.');
    setShowError(true);
  }
};

const checkCameraPermissionSilently = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    stream.getTracks().forEach(track => track.stop());
    setCameraPermission(true);
    localStorage.setItem('cameraPermissionGranted', 'true');
    setScanning(true); // ← just set state, don't call startScanner()
  } catch (error) {
    localStorage.removeItem('cameraPermissionGranted');
    setCameraPermission(false);
  }
};
 

  const handleScan = async (decodedText) => {
    try {
      // Stop scanner immediately to prevent multiple scans
      if (scannerRef.current) {
        await scannerRef.current.clear();
        setScanning(false);
      }

      const response = await attendanceAPI.scanQR(decodedText);

      if (response.data.success) {
        setShowSuccess(true);
        setTodayAttendance({ hasAttended: true, ...response.data.data });
        
        // Auto-hide success message after 3 seconds
        setTimeout(() => setShowSuccess(false), 3000);
      }
        } catch (error) {
  const status = error.response?.status;
  const serverMessage = error.response?.data?.error;

  let userMessage;
  let shouldRestart = true;

  if (status === 401) {
    userMessage = 'Your session has expired. Please log out and log back in to scan attendance.';
    shouldRestart = false;
  } else if (status === 403) {
    userMessage = serverMessage || 'You are not authorised to perform this action.';
    shouldRestart = false;
  } else if (status === 400 && serverMessage?.toLowerCase().includes('qr code has expired')) {
    userMessage = 'This QR code has expired. Please ask your teacher to generate a new one.';
    shouldRestart = false;
  } else if (status === 400 && serverMessage?.toLowerCase().includes('already marked')) {
    userMessage = 'You have already marked your attendance today.';
    shouldRestart = false;
  } else {
    userMessage = serverMessage || 'Something went wrong. Please try again.';
  }

  setGeneralErrorMessage(userMessage);
  setShowGeneralError(true);

  setTimeout(() => {
    setShowGeneralError(false);
    if (shouldRestart && !todayAttendance?.hasAttended) {
     setScanning(true);
    }
  }, 5000);
}
  };

  const stopScanner = async () => {
  if (scannerRef.current) {
    await scannerRef.current.clear();
    scannerRef.current = null;
  }
  setScanning(false);
};

  if (loading) {
    return (
      <div className="sa-loading-container">
        <div className="sa-loading-spinner"></div>
        <p className="sa-loading-text">Loading...</p>
      </div>
    );
  }

  if (todayAttendance?.hasAttended) {
    return (
      <div className="sa-wrapper">
        <div className="sa-container">
          <div className="sa-card">
            <div className="sa-card-body">
              <div className="sa-success-state">
                <div className="sa-icon-circle sa-icon-circle-success">
                  <CheckCircleFill className="sa-icon sa-icon-success" />
                </div>
                <h3 className="sa-title sa-title-success">Attendance Already Marked!</h3>
                <p className="sa-message">
                  You have successfully marked your attendance for today.
                </p>
                <div className="sa-details-card">
                  <p className="sa-detail-item">
                    <span className="sa-detail-label">Status:</span>{' '}
                    <span className="sa-badge sa-badge-success">
                      {todayAttendance.status || 'Present'}
                    </span>
                  </p>
                  <p className="sa-detail-item">
                    <span className="sa-detail-label">Date:</span> {new Date().toLocaleDateString()}
                  </p>
                  <p className="sa-detail-item">
                    <span className="sa-detail-label">Time:</span>{' '}
                    {todayAttendance.createdAt 
                      ? new Date(todayAttendance.createdAt).toLocaleTimeString()
                      : new Date().toLocaleTimeString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sa-wrapper">
      <div className="sa-container">
        <div className="sa-card">
          <div className="sa-card-body">
            <div className="sa-header">
              <div className="sa-icon-circle sa-icon-circle-primary">
                <QrCodeScan className="sa-icon sa-icon-primary" />
              </div>
              <h2 className="sa-title">Scan Classroom QR Code</h2>
              <p className="sa-subtitle">
                Welcome back, <span className="sa-highlight">{user.name}</span>!
                <br />
                Scan the QR code displayed in your classroom to mark attendance.
              </p>
            </div>

          

           {!scanning ? (
            <div className="sa-action-section">
              <button 
                className="sa-btn sa-btn-primary"
                onClick={requestCameraPermission}
              >
                <Camera className="sa-btn-icon" />
                Start Camera & Scan QR
              </button>
              <p className="sa-hint">
                Make sure you're in the classroom and the QR code is clearly visible
              </p>
            </div>
          ) : (
            <div className="sa-scanner-section">
              <div className="sa-qr-reader-container">
                <div id="sa-qr-reader" className="sa-qr-reader"></div>
              </div>
              <div className="sa-camera-label">
                <CameraVideo className="me-2" size={16} />
                Select Camera Source
              </div>
              <div className="sa-scanner-controls">
                <button 
                  className="sa-btn sa-btn-outline sa-btn-danger"
                  onClick={stopScanner}
                >
                  Stop Scanner
                </button>
              </div>
            </div>
          )}

            <div className="sa-instructions">
              <h3 className="sa-instructions-title">
                <Lightbulb className="me-2" size={20} />
                Instructions:
              </h3>
              <ul className="sa-instructions-list">
                <li className="sa-instruction-item">
                  <QrCodeScan className="me-2" size={16} />
                  Position the QR code within the scanning frame
                </li>
                <li className="sa-instruction-item">
                  <Lightbulb className="me-2" size={16} />
                  Ensure good lighting for better scanning
                </li>
                <li className="sa-instruction-item">
                  <Phone className="me-2" size={16} />
                  Hold your phone steady for 1-2 seconds
                </li>
                <li className="sa-instruction-item">
                  <CheckCircle className="me-2" size={16} />
                  Attendance will be marked automatically when scanned
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <Modal 
        show={showSuccess} 
        centered 
        onHide={() => setShowSuccess(false)}
        className="sa-modal"
        dialogClassName="sa-modal-dialog"
        contentClassName="sa-modal-content"
      >
        <Modal.Body className="sa-modal-body">
          <div className="sa-modal-icon">
            <CheckCircleFill className="sa-modal-icon-success" />
          </div>
          <h4 className="sa-modal-title sa-modal-title-success">Success!</h4>
          <p className="sa-modal-message">Your attendance has been marked for today.</p>
          <Button 
            variant="primary" 
            className="sa-modal-btn sa-modal-btn-success"
            onClick={() => setShowSuccess(false)}
          >
            Done
          </Button>
        </Modal.Body>
      </Modal>

            {/* General Error Modal */}
      <Modal 
        show={showGeneralError} 
        centered 
        onHide={() => setShowGeneralError(false)}
        className="sa-modal"
        dialogClassName="sa-modal-dialog"
        contentClassName="sa-modal-content"
      >
        <Modal.Body className="sa-modal-body">
          <div className="sa-modal-icon">
            <ExclamationTriangleFill className="sa-modal-icon-error" />
          </div>
          <h4 className="sa-modal-title sa-modal-title-error">Error</h4>
          <p className="sa-modal-message">{generalErrorMessage}</p>
          <Button 
            variant="primary" 
            className="sa-modal-btn sa-modal-btn-primary"
            onClick={() => {
              setShowGeneralError(false);
              if (!todayAttendance?.hasAttended) {
                setScanning(true);
              }
            }}
          >
            Try Again
          </Button>
        </Modal.Body>
      </Modal>

          {/* Camera Permission Modal */}
      <Modal 
        show={showError} 
        centered 
        onHide={() => setShowError(false)}
        className="sa-modal"
        dialogClassName="sa-modal-dialog"
        contentClassName="sa-modal-content"
      >
        <Modal.Body className="sa-modal-body">
          <div className="sa-modal-icon">
            <Camera className="sa-modal-icon-error" style={{ color: 'var(--sa-primary)' }} />
          </div>
          <h4 className="sa-modal-title" style={{ color: 'var(--sa-primary)' }}>Camera Access Required</h4>
          <p className="sa-modal-message">{errorMessage}</p>
          <Button 
            variant="primary" 
            className="sa-modal-btn sa-modal-btn-primary"
            onClick={() => {
              setShowError(false);
              requestCameraPermission();
            }}
          >
            Request Camera Permission
          </Button>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default StudentAttendance;